import { createContext, PropsWithChildren, useContext, useMemo } from 'react';

import { Connect } from '@rover/react-lib/src/components/utils/Redux';
import usePageContext from '@rover/react-lib/src/context/usePageContext';
import { selectors as searchFilterSelectors } from '@rover/react-lib/src/pages/search/SearchPage/SearchFilters.duck';
import useIsBrightHorizonsNavigation from '@rover/shared/js/hooks/useIsBrightHorizonsNavigation';
import getStartDateFromQueryParams from '@rover/shared/js/utils/getDateFromQueryParams';
import { DateRangeField } from '@rover/types/src/datetime/DateRange';

type BHNavbarContextProps = {
  startDate: DateRangeField;
  pageName: string;
  isBrightHorizonsNavigation: boolean;
};

const selector = (state) => ({
  startDate: searchFilterSelectors.getFilters(state).startDate,
});

const BHNavbarContext = createContext<BHNavbarContextProps>({} as BHNavbarContextProps);

type BHNavbarProviderProps = PropsWithChildren<{
  startDate: DateRangeField;
}>;

const BHNavbarContextProvider = ({ children, startDate }: BHNavbarProviderProps): JSX.Element => {
  const isBrightHorizonsNavigation = useIsBrightHorizonsNavigation();
  const { pageName } = usePageContext();
  const memoizedStartDate = useMemo(() => startDate || getStartDateFromQueryParams(), [startDate]);

  const contextValue = useMemo(
    () => ({
      startDate: memoizedStartDate,
      isBrightHorizonsNavigation,
      pageName,
    }),
    [isBrightHorizonsNavigation, memoizedStartDate, pageName]
  );

  return <BHNavbarContext.Provider value={contextValue}>{children}</BHNavbarContext.Provider>;
};

const BHNavbarProvider = ({ children }: PropsWithChildren): JSX.Element => {
  return (
    <Connect selector={selector}>
      {({ startDate }) => (
        <BHNavbarContextProvider startDate={startDate}>{children}</BHNavbarContextProvider>
      )}
    </Connect>
  );
};

export const useBHNavbarContext = (): BHNavbarContextProps => {
  const context = useContext(BHNavbarContext);

  if (!context) {
    throw new Error('useBHNavbarContext must be used within a BHNavbarContextProvider');
  }
  return context;
};

export default BHNavbarProvider;
